import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BedDouble, Bath, Ruler, MapPin, Search, Loader2, Building2, Eye } from 'lucide-react';
import PageHero from '../../components/public/PageHero';
import Reveal from '../../components/public/Reveal';
import api from '../../lib/api';
import { DEPARTMENTS, PROPERTY_TYPES, OPERATIONS, OPERATION_LABELS, PROPERTY_TYPE_LABELS } from '../../lib/constants';

const OP_BADGE = {
  venta: 'bg-orange-500',
  alquiler: 'bg-cnib-blue',
  anticretico: 'bg-emerald-500',
};

export default function Propiedades() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ operation: '', property_type: '', city: '', q: '' });

  const load = () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api
      .get('/properties', { params })
      .then(({ data }) => setProperties(data))
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [filters.operation, filters.property_type, filters.city]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const selectCls =
    'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none transition-all focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div>
      <PageHero
        eyebrow="MLS CNIB"
        title="Propiedades publicadas por la red de socios"
        description="Casas, departamentos, terrenos y espacios comerciales verificados por profesionales de la Cámara en todo Bolivia."
      />

      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3" data-testid="property-filters">
            <select value={filters.operation} onChange={(e) => setFilters({ ...filters, operation: e.target.value })} className={selectCls} data-testid="filter-operation-select">
              <option value="">Operación</option>
              {OPERATIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select value={filters.property_type} onChange={(e) => setFilters({ ...filters, property_type: e.target.value })} className={selectCls} data-testid="filter-type-select">
              <option value="">Tipo</option>
              {PROPERTY_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <select value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className={selectCls} data-testid="filter-city-select">
              <option value="">Departamento</option>
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <div className="relative flex-1 min-w-52">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Buscar por zona o título…"
                className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-11 pr-4 text-sm outline-none transition-all focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15"
                data-testid="filter-search-input"
              />
            </div>
            <button type="submit" className="rounded-full bg-cnib-navy px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-cnib-blue" data-testid="filter-search-button">
              Buscar
            </button>
          </form>

          {loading ? (
            <div className="flex justify-center py-24" data-testid="properties-loading">
              <Loader2 className="h-10 w-10 animate-spin text-cnib-blue" />
            </div>
          ) : properties.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 py-24 text-center" data-testid="properties-empty">
              <Building2 className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-4 text-slate-500">No hay propiedades con esos filtros. Prueba otra búsqueda.</p>
            </div>
          ) : (
            <div className="mt-10 grid gap-7 sm:grid-cols-2 lg:grid-cols-3" data-testid="properties-grid">
              {properties.map((p, i) => (
                <Reveal key={p.property_id} delay={(i % 3) * 0.08}>
                  <Link
                    to={`/propiedades/${p.property_id}`}
                    data-testid={`property-card-${p.property_id}`}
                    className="group block h-full overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="relative h-56 overflow-hidden bg-slate-100">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <Building2 className="h-12 w-12 text-slate-300" />
                        </div>
                      )}
                      <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide text-white ${OP_BADGE[p.operation]}`}>
                        {OPERATION_LABELS[p.operation]}
                      </span>
                      {p.featured && (
                        <span className="absolute right-4 top-4 rounded-full bg-cnib-navy/90 px-3 py-1 text-xs font-bold text-orange-400">
                          Destacada
                        </span>
                      )}
                    </div>
                    <div className="p-6">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{PROPERTY_TYPE_LABELS[p.property_type]}</p>
                      <h3 className="mt-1 line-clamp-2 font-heading text-lg font-bold text-slate-900 transition-colors group-hover:text-cnib-blue">
                        {p.title}
                      </h3>
                      <p className="mt-1.5 flex items-center gap-1 text-sm text-slate-500">
                        <MapPin className="h-3.5 w-3.5 text-orange-500" /> {p.zone ? `${p.zone}, ` : ''}{p.city}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <p className="font-heading text-xl font-extrabold text-cnib-blue">
                          US$ {Number(p.price_usd).toLocaleString('es-BO')}
                          {p.operation === 'alquiler' && <span className="text-xs font-semibold text-slate-400"> /mes</span>}
                        </p>
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Eye className="h-3.5 w-3.5" /> {p.views}
                        </span>
                      </div>
                      {(p.bedrooms > 0 || p.bathrooms > 0 || p.area_m2 > 0) && (
                        <div className="mt-3 flex items-center gap-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
                          {p.bedrooms > 0 && <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" /> {p.bedrooms}</span>}
                          {p.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" /> {p.bathrooms}</span>}
                          {p.area_m2 > 0 && <span className="flex items-center gap-1"><Ruler className="h-3.5 w-3.5" /> {p.area_m2} m²</span>}
                        </div>
                      )}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
